import { bootstrapApplication } from '@angular/platform-browser';
import { AppComponent } from 'app/app.component';
import { appConfig } from 'app/app.config';

import * as Sentry from '@sentry/angular';
import { environment } from 'environments/environment';
import { isDevMode, provideZoneChangeDetection } from '@angular/core';
import { scrubSensitiveData } from 'app/modules/shared/utils/safe-log';

// Only a developer's own machine (ng serve on localhost) prints debug output. Every deployed build - production,
// test and development - is silent, so no personal data ends up in a user's browser console (BSI O.Source_3).
// console.error and console.warn are kept for error reporting; they only carry short messages (see safe-log.ts).
const isLocalMachine = ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname);
if (!isDevMode() || !isLocalMachine) {
  console.log = () => {};
  console.debug = () => {};
  console.info = () => {};
}

if (!isDevMode()) {
    console.log('-- sentry --');
    const runtimeDsn = (window as any).__ENV?.SENTRY_DSN as string | undefined;
    const DNS = runtimeDsn || environment.sentryDSN;
    if (!DNS) {
        console.warn('Sentry DSN missing (expected runtime assets/env.js from SENTRY_DSN)');
    }
    Sentry.init({
        dsn: DNS,
        environment: environment.name,
        integrations: (defaults) => [
            // The default 'Console' integration copies every console call, with its arguments, into the
            // breadcrumbs of the next error report: leave it out (BSI O.Source_3)
            ...defaults.filter((integration) => integration.name !== 'Console'),
            // Registers and configures the Tracing integration,
            // which automatically instruments your application to monitor its
            // performance, including custom Angular routing instrumentation
            Sentry.browserTracingIntegration(),
            // Sentry.browserProfilingIntegration(),
            // Registers the Replay integration,
            // which automatically captures Session Replays
            // Sentry.replayIntegration(),
        ],
        // Tokens in request addresses (emergency-agent-token, phone-access-token, ...) must not reach the error reports
        beforeSend: (event) => scrubSensitiveData(event),
        beforeSendTransaction: (event) => scrubSensitiveData(event),
        beforeSendSpan: (span) => scrubSensitiveData(span),
        beforeBreadcrumb: (breadcrumb) => scrubSensitiveData(breadcrumb),
        // Tracing
        // Set tracesSampleRate to 1.0 to capture 100%
        // of transactions for tracing.
        // We recommend adjusting this value in production
        tracesSampleRate: 1.0, //  Capture 100% of the transactions
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        // tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],
        tracePropagationTargets: environment.sentry.tracePropagationTargets,
        // Session Replay
        // Capture Replay for 10% of all sessions,
        // plus for 100% of sessions with an error
        replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
        // Set profilesSampleRate to 1.0 to profile every transaction.
        // Since profilesSampleRate is relative to tracesSampleRate,
        // the final profiling rate can be computed as tracesSampleRate * profilesSampleRate
        // For example, a tracesSampleRate of 0.5 and profilesSampleRate of 0.5 would
        // results in 25% of transactions being profiled (0.5*0.5=0.25)
        // profilesSampleRate: 1.0,
        // Sentry 11 replaced `sendDefaultPii` by `dataCollection` and collects more by default.
        // Explicitly keep the former `sendDefaultPii: false` behaviour (no IP, cookies or request/response bodies).
        dataCollection: {
            userInfo: false,
            cookies: false,
            httpHeaders: {
                request: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
                response: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
            },
            httpBodies: [],
            urlQueryParams: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
            genAI: { inputs: false, outputs: false },
            databaseQueryData: false,
            graphQL: { document: false, variables: false },
        },
    });
}

bootstrapApplication(AppComponent, {...appConfig, providers: [provideZoneChangeDetection(), ...appConfig.providers]}).catch((err) =>
    console.error(err)
);
